// Lightweight wrapper around piexifjs for client-side EXIF stripping.
// Strategy:
//   - JPEG (image/jpeg): read DateTimeOriginal first, then use piexif.remove on
//     the data URL and convert back to a Blob.
//   - PNG / WEBP / anything else: pass through unchanged, takenAt = null.
// Falls back to the original file on any failure (we never want to block uploads
// because EXIF stripping had a bad day).

import piexif from "piexifjs";

const isJpeg = (file) => {
  if (!file) return false;
  if (file.type === "image/jpeg" || file.type === "image/jpg") return true;
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".jpg") || name.endsWith(".jpeg");
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = (dataUrl, type) => {
  const base64 = dataUrl.split(",")[1] || "";
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: type || "image/jpeg" });
};

// Convert EXIF DateTimeOriginal ("YYYY:MM:DD HH:MM:SS") to ISO 8601 local
// ("YYYY-MM-DDTHH:MM:SS", no Z suffix). Returns null on any parse failure.
const exifDateToIso = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  // Expected format: "YYYY:MM:DD HH:MM:SS"
  const m = raw.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  // Sanity-check ranges; EXIF "0000:00:00 00:00:00" is a known sentinel for
  // "no date" used by some cameras/editors — treat as null.
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (year < 1970 || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  return `${y}-${mo}-${d}T${h}:${mi}:${s}`;
};

const readTakenAtFromJpeg = (dataUrl) => {
  try {
    const exif = piexif.load(dataUrl);
    const exifIfd = exif && exif.Exif;
    if (!exifIfd) return null;
    const raw = exifIfd[piexif.ExifIFD.DateTimeOriginal];
    return exifDateToIso(raw);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("EXIF DateTimeOriginal read failed.", err);
    return null;
  }
};

/**
 * Returns a Blob with EXIF stripped (for JPEG) or the original file (for non-JPEG),
 * along with the EXIF DateTimeOriginal as ISO 8601 local time string (or null).
 * @param {File} file
 * @returns {Promise<{blob: Blob, takenAt: string | null}>}
 */
export const stripExif = async (file) => {
  if (!isJpeg(file)) {
    return { blob: file, takenAt: null };
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    const takenAt = readTakenAtFromJpeg(dataUrl);
    let blob;
    try {
      const cleaned = piexif.remove(dataUrl);
      blob = dataUrlToBlob(cleaned, "image/jpeg");
    } catch (removeErr) {
      // eslint-disable-next-line no-console
      console.warn("EXIF strip failed; uploading original file.", removeErr);
      blob = file;
    }
    return { blob, takenAt };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("EXIF processing failed; uploading original file.", err);
    return { blob: file, takenAt: null };
  }
};

export default stripExif;
